import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import GoogleSignInButton from '../Auth/GoogleSignInButton';
import '../Conversation/ConversationPage.css';

const ProfilePage = () => {
  const { user, logout, subscribe, linkGoogle } = useAuth();
  const navigate = useNavigate();
  const [linkingGoogle, setLinkingGoogle] = useState(false);
  const [googleError, setGoogleError] = useState(null);

  // Check if Google OAuth is configured
  const isGoogleConfigured = process.env.REACT_APP_GOOGLE_CLIENT_ID &&
    process.env.REACT_APP_GOOGLE_CLIENT_ID !== 'your-google-client-id-here.apps.googleusercontent.com';

  const handleLogout = () => {
    logout();
    navigate('/sync');
  };

  const handleSubscribe = async () => {
    const result = await subscribe();
    if (result.success) {
      alert('Successfully upgraded to Premium!');
    } else {
      alert('Subscription failed: ' + result.error);
    }
  };

  const handleGoogleSuccess = async (googleIdToken) => {
    setLinkingGoogle(true);
    setGoogleError(null);

    const result = await linkGoogle(googleIdToken);

    if (result.success) {
      // Success! User's Google account is now linked
      // User object will be updated automatically via refreshUser()
    } else {
      setGoogleError(result.error || 'Failed to link Google account');
    }

    setLinkingGoogle(false);
  };

  const handleGoogleError = (error) => {
    setGoogleError(error || 'Google Sign-In was cancelled or failed');
    setLinkingGoogle(false);
  };

  const trialDaysLeft = user?.trialEndsAt
    ? Math.max(0, Math.ceil((new Date(user.trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;

  return (
    <div className="page-container">
      <header className="page-header">
        <button className="btn-back" onClick={() => navigate('/home')}>
          ← Back
        </button>
        <h1>Profile</h1>
      </header>

      <main className="page-content">
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            marginBottom: '24px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
              color: 'white',
              margin: '0 auto 24px auto'
            }}>
              {user?.name?.[0]?.toUpperCase()}
            </div>

            <h2 style={{ textAlign: 'center', marginBottom: '8px' }}>
              {user?.name}
            </h2>

            <p style={{ textAlign: 'center', color: '#757575', marginBottom: '24px' }}>
              {user?.email}
            </p>

            {user?.hasPremium ? (
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '16px',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <p style={{ margin: 0, fontWeight: 600 }}>✨ Premium Member</p>
                {user?.userRole === 'teacher' && (
                  <p style={{ margin: '8px 0 0 0', fontSize: '12px' }}>
                    Teaching accounts are always free
                  </p>
                )}
                {user?.userRole === 'student' && user?.subscribedAt && (
                  <p style={{ margin: '8px 0 0 0', fontSize: '12px' }}>
                    Member since {new Date(user.subscribedAt).toLocaleDateString()}
                  </p>
                )}
              </div>
            ) : (
              <div style={{
                background: '#E3F2FD',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <p style={{ margin: '0 0 8px 0', fontWeight: 600 }}>
                  Free Trial Active
                </p>
                <p style={{ margin: 0, fontSize: '14px', color: '#757575' }}>
                  {trialDaysLeft} days remaining
                </p>
              </div>
            )}
          </div>

          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            marginBottom: '24px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ marginTop: 0 }}>Account Information</h3>

            <div style={{ marginBottom: '16px' }}>
              <p style={{
                fontSize: '12px',
                fontWeight: 600,
                color: '#757575',
                margin: '0 0 4px 0'
              }}>
                JOINED
              </p>
              <p style={{ margin: 0 }}>
                {new Date(user?.createdAt).toLocaleDateString()}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p style={{
                fontSize: '12px',
                fontWeight: 600,
                color: '#757575',
                margin: '0 0 4px 0'
              }}>
                SYNC CODE
              </p>
              <p style={{ margin: 0, fontFamily: 'monospace' }}>
                {user?.syncCode || 'Not generated'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p style={{
                fontSize: '12px',
                fontWeight: 600,
                color: '#757575',
                margin: '0 0 4px 0'
              }}>
                GOOGLE ACCOUNT
              </p>
              {user?.googleEmail ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#4caf50', fontSize: '18px' }}>✓</span>
                  <p style={{ margin: 0 }}>{user.googleEmail}</p>
                </div>
              ) : (
                <div style={{ marginTop: '8px' }}>
                  {isGoogleConfigured ? (
                    <div style={{ maxWidth: '280px' }}>
                      <GoogleSignInButton
                        onSuccess={handleGoogleSuccess}
                        onError={handleGoogleError}
                        text="continue_with"
                      />
                      {linkingGoogle && (
                        <p style={{
                          margin: '8px 0 0 0',
                          fontSize: '12px',
                          color: '#1976d2',
                          fontStyle: 'italic'
                        }}>
                          Linking account...
                        </p>
                      )}
                      {googleError && (
                        <p style={{
                          margin: '8px 0 0 0',
                          fontSize: '12px',
                          color: '#d32f2f'
                        }}>
                          {googleError}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p style={{
                      margin: '8px 0 0 0',
                      fontSize: '13px',
                      color: '#d32f2f',
                      fontStyle: 'italic'
                    }}>
                      Google OAuth not configured. Set REACT_APP_GOOGLE_CLIENT_ID in .env file.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {!user?.hasPremium && (
            <button
              onClick={handleSubscribe}
              style={{
                width: '100%',
                padding: '16px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                marginBottom: '16px',
                transition: 'transform 0.2s'
              }}
            >
              Upgrade to Premium - $5.99/month
            </button>
          )}

          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              padding: '16px',
              background: 'white',
              color: '#d32f2f',
              border: '2px solid #d32f2f',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Log Out
          </button>
        </div>
      </main>
    </div>
  );
};

export default ProfilePage;
